export type ProductFeature = { name: string; description: string };

export type ProductPlatform = 'web' | 'android';
export type ProductStatus = 'live' | 'coming-soon';

export type Product = {
  slug: 'ats-resume-helper' | 'acadly-ai' | 'viralcut' | 'us-citizenship-test' | 'canadian-citizenship-test' | 'ludovest' | 'balancely' | 'uk-citizenship-test' | 'arrow-escape' | 'police-chief' | 'repzo' | 'digital-khata' | 'sudoku-cove' | 'aqua-bliss' | 'wonderfind' | 'xo-arena' | 'petalgrove';
  name: string;
  title: string;
  description: string;
  tagline: string;
  oneLiner: string;
  liveUrl: string;
  /** Marketing website for Android apps that also have one (liveUrl stays the Google Play listing). */
  websiteUrl?: string;
  logoSrc: string;
  faviconSrc: string;
  problem: string;
  features: ProductFeature[];
  techStack: string[];
  builtWithServiceSlugs: string[];
  relatedBlogSlugs: string[];
  primaryKeyword: string;
  theme: 'dark' | 'light';
  /** Defaults to 'web'. 'android' products link to Google Play instead of a live site. */
  platform?: ProductPlatform;
  /** Defaults to 'live'. Coming-soon products link to their Orvexaco detail page. */
  status?: ProductStatus;
  /** App-specific legal pages hosted on this site (used by mobile apps). */
  legal?: { privacyPath: string; termsPath: string };
  supportEmail?: string;
  /** Short hero line shown under the CTAs, e.g. "No ads · Offline · No sign-in". */
  heroNote?: string;
  /** Number of screenshot placeholders to render on the product page. */
  screenshotSlots?: number;
  /** Real screenshots for the product page; takes precedence over screenshotSlots. */
  screenshots?: { src: string; alt: string }[];
  /** 'scroll' renders screenshots as a horizontally scrollable strip instead of a grid. Defaults to 'grid'. */
  screenshotLayout?: 'grid' | 'scroll';
  /** Per-page Open Graph / social image (site-relative path); defaults to the Orvexaco logo. */
  ogImage?: string;
};

export const products: Product[] = [
  {
    slug: 'ats-resume-helper',
    name: 'ATS Resume Helper',
    title: 'ATS Resume Helper — AI Resume Checker & Builder',
    description:
      'ATS Resume Helper: AI-powered ATS resume scoring, keyword gap analysis, ATS-safe resume builder, and job-specific cover letters. Built and shipped by Orvexaco.',
    tagline: 'Beat the ATS. Land more interviews.',
    oneLiner: 'AI resume checker, ATS-safe builder, and job-specific cover letter generator.',
    liveUrl: 'https://atsresumehelper.com',
    logoSrc: '/logos/atsresumehelper-160.webp',
    faviconSrc: '/logos/atsresumehelper-icon-32.webp',
    primaryKeyword: 'ATS resume checker',
    theme: 'dark',
    problem:
      'Applicant Tracking Systems (ATS) reject 75% of resumes before a human ever reads them. Most resume tools fix formatting but ignore the real problem — keyword fit to the job description. ATS Resume Helper does both: it parses your resume the way a real ATS does, scores it against any job posting, and shows the exact gaps to close.',
    features: [
      { name: 'ATS-Style Score (0–100)', description: 'Real ATS-grade parsing and scoring — not a formatting linter.' },
      { name: 'Keyword Gap Analysis', description: 'Mirrors the language of the job description and shows the missing terms by priority.' },
      { name: 'ATS-Safe Resume Builder', description: 'Single-column, parser-friendly templates that rank in every major ATS platform.' },
      { name: 'AI Cover Letter Generator', description: 'Job-specific cover letters that hit the keywords without sounding stuffed.' },
      { name: 'Export-Ready Downloads', description: 'PDF and DOCX exports clean enough for both human reviewers and parsers.' },
      { name: 'No Fabricated Experience', description: 'Reframes what is genuinely on your CV — never invents experience that gets caught later.' },
    ],
    techStack: [
      'Next.js + React',
      'Anthropic Claude (LLM)',
      'PostgreSQL + pgvector',
      'PDF/DOCX parsing pipeline',
      'Stripe (billing)',
      'Vercel + AWS S3',
    ],
    builtWithServiceSlugs: ['ai-agents', 'ai-automation', 'custom-saas-development'],
    relatedBlogSlugs: ['ats-resume-helper-how-it-works', 'how-to-choose-saas-development-partner'],
  },
  {
    slug: 'acadly-ai',
    name: 'Acadly AI',
    title: 'Acadly AI — One AI Workspace for Academic Research',
    description:
      'Acadly AI: research writing, slide generation, AI detection, plagiarism check, paraphrasing, document chat. The academic AI workspace, built and shipped by Orvexaco.',
    tagline: 'One AI workspace for serious academic scholarship.',
    oneLiner: 'Research writing, slides, citations, plagiarism, AI detection, document chat — all academic, all in one place.',
    liveUrl: 'https://acadlyai.com',
    logoSrc: '/logos/acadlyai.svg',
    faviconSrc: '/logos/acadlyai-fav.svg',
    primaryKeyword: 'AI for academic research',
    theme: 'light',
    problem:
      'General-purpose chatbots can write a paper. They cannot check plagiarism, ground citations to real sources, generate matching slide decks, or chat across your PDF library. Researchers end up stitching together 4–5 separate tools just to ship one paper. Acadly AI replaces that stack with a single academic-tuned workspace.',
    features: [
      { name: 'AI Research Writer', description: 'Generates structured drafts with cited sources from indexed academic literature.' },
      { name: 'Slide Generator', description: 'Convert any paper or topic into a presentation-ready slide deck with structured layouts.' },
      { name: 'Chart Builder', description: 'Generate matching charts from data tables — no exporting between tools.' },
      { name: 'Plagiarism Checker', description: 'Submission-ready plagiarism check, built into the same workflow as the editor.' },
      { name: 'AI Detector + Humanizer', description: 'Flag AI-detection signal and pass drafts through a humanizer that preserves meaning.' },
      { name: 'Document Chat', description: 'Upload your PDF library and chat across it with citation-grounded answers.' },
      { name: 'Grammar + Paraphrase', description: 'Academic-tuned grammar check and paraphrase that respect domain language.' },
    ],
    techStack: [
      'Next.js + React',
      'Anthropic Claude + multi-model routing',
      'pgvector RAG pipeline',
      'PDF parsing + citation extraction',
      'Stripe (billing)',
      'Vercel + AWS',
    ],
    builtWithServiceSlugs: ['ai-agents', 'ai-automation', 'custom-saas-development'],
    relatedBlogSlugs: ['acadly-ai-vs-chatgpt-for-research', 'claude-vs-gpt-for-business-automation'],
  },
  {
    slug: 'viralcut',
    name: 'Viral Cut',
    title: 'Viral Cut — AI Video Clipping & Short-Form Publishing',
    description:
      'Viral Cut: turn one long video into ranked viral shorts with AI clipping, karaoke captions, auto-reframe, B-roll, hooks, and TikTok scheduling. Built and shipped by Orvexaco.',
    tagline: '1 long video, 10 viral clips.',
    oneLiner: 'AI video clipping, captions, reframe, and TikTok scheduling — from podcast to shorts in one click.',
    liveUrl: 'https://viralcut.click',
    logoSrc: '/logos/viralcut.svg',
    faviconSrc: '/logos/viralcut-fav.svg',
    primaryKeyword: 'AI video clipping tool',
    theme: 'dark',
    problem:
      'Long-form creators sit on hours of archive content while short-form demand keeps rising. Manual editing — finding hooks, reframing to 9:16, captioning, and scheduling — does not scale past a few clips a week. Viral Cut automates the full pipeline: viral-moment detection ranked by Viral Score, branded captions, speaker tracking, and publish-ready exports straight to TikTok.',
    features: [
      { name: 'ClipAnything', description: 'Scans speech, scene, and emotion to surface the moments most likely to pop — across podcasts, interviews, gaming, and vlogs.' },
      { name: 'Viral Score Ranking', description: 'Every candidate clip gets a 0–100 Viral Score so you post the highest-potential hooks first.' },
      { name: 'ReframeAnything', description: 'Auto-tracks the speaker and keeps them centered for 9:16, 1:1, or 16:9 — no manual keyframing.' },
      { name: 'Animated Captions', description: 'Word-by-word karaoke, bold, box, and simple styles tuned for retention on TikTok and Reels.' },
      { name: 'AI B-roll & Auto-Hook', description: 'On-beat cutaways from your transcript plus opening hooks generated to stop the scroll.' },
      { name: 'TikTok Scheduling', description: 'Drag clips onto a calendar and auto-publish from one connected account.' },
      { name: 'Brand Templates', description: 'Lock logo, fonts, and colors so every clip stays on-brand — built for teams and agencies.' },
    ],
    techStack: [
      'Next.js + React',
      'Video processing + FFmpeg pipeline',
      'Speech / scene / emotion models',
      'YouTube import + transcription',
      'Stripe (credit-based billing)',
      'TikTok publishing API',
      'Vercel + cloud render queue',
    ],
    builtWithServiceSlugs: ['ai-automation', 'ai-agents', 'custom-saas-development'],
    relatedBlogSlugs: ['viral-cut-long-video-to-short-clips', 'claude-vs-gpt-for-business-automation', 'how-to-choose-saas-development-partner'],
  },
  {
    slug: 'us-citizenship-test',
    name: 'US Citizenship Test 2026',
    title: 'US Citizenship Test 2026 — Civics Test Prep App for Android',
    description:
      'US Citizenship Test 2026: all 128 official civics questions, 1,400+ practice questions, flashcards with spaced repetition, and a realistic 5-part mock interview. Offline, no ads, no account. Android app built and shipped by Orvexaco.',
    tagline: 'All 128 official civics questions, mock interview, flashcards. Offline, no ads.',
    oneLiner: 'Offline study kit for the 2025 U.S. civics test — 128 official questions, mock interview, flashcards, no ads.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.uscitizenshiptest.app',
    logoSrc: '/logos/us-citizenship-test-160.webp',
    faviconSrc: '/logos/us-citizenship-test-icon-32.webp',
    primaryKeyword: 'US citizenship test app',
    theme: 'dark',
    platform: 'android',
    legal: { privacyPath: '/us-citizenship-test/privacy', termsPath: '/us-citizenship-test/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    problem:
      'Pass your U.S. naturalization interview with confidence. US Citizenship Test 2026 is a complete, offline study kit built around the official 2025 civics test: all 128 official questions with every accepted answer, 1,400+ practice questions, instant feedback and explanations, flashcards with spaced repetition, reading and writing drills, progress by category, your state\'s officials filled in automatically, and a realistic 5-part mock interview (standard and 65/20 rules). Free with a one-time Premium unlock — no subscription, no account, no ads, no tracking.',
    features: [
      { name: 'All 128 Official Questions', description: 'Every 2025 civics question with every accepted answer and a plain-English explanation.' },
      { name: '1,400+ Practice Questions', description: 'Three practice modes with instant feedback, so you learn from every miss.' },
      { name: 'Full 5-Part Mock Interview', description: 'Realistic interview flow with the 2025 pass rules (12/20, and 65/20 → 6/10).' },
      { name: 'Flashcards With Spaced Repetition', description: 'Cards resurface on a schedule tuned to what you keep getting wrong.' },
      { name: 'Reading & Writing Drills', description: 'Official USCIS vocabulary for the English reading and writing portions.' },
      { name: 'Offline, No Account, No Ads', description: 'Works fully offline. One-time Premium unlock — no subscription, no tracking.' },
    ],
    techStack: [
      'Kotlin + Jetpack Compose',
      'Room + DataStore (offline-first)',
      'Google Play Billing + RevenueCat',
      'Android TTS',
      'WorkManager (local reminders)',
    ],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design', 'ai-agents'],
    relatedBlogSlugs: ['citizenship-test-prep-apps-us-canada-uk', 'mobile-app-development-cost-2026', 'how-to-choose-saas-development-partner'],
  },
  {
    slug: 'canadian-citizenship-test',
    name: 'Canadian Citizenship Test 2026',
    title: 'Canadian Citizenship Test 2026 — Discover Canada Test Prep App for Android',
    description:
      'Canadian Citizenship Test 2026: 1,000+ practice questions from the official Discover Canada guide, all 10 chapters in 48 illustrated lessons, timed mock tests in the real 20-question format, and province-specific questions. Offline, no ads, no account. Android app built and shipped by Orvexaco.',
    tagline: '1,000+ Discover Canada questions, timed mock tests, all 10 chapters. Offline, no ads.',
    oneLiner: 'Offline study kit for the Canadian citizenship test — 1,000+ Discover Canada questions, timed mocks, 48 lessons, no ads.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.orvexaco.canadiancitizenshiptest',
    logoSrc: '/logos/canadian-citizenship-test-160.webp',
    faviconSrc: '/logos/canadian-citizenship-test-icon-32.webp',
    primaryKeyword: 'Canadian citizenship test app',
    theme: 'dark',
    platform: 'android',
    legal: { privacyPath: '/canadian-citizenship-test/privacy', termsPath: '/canadian-citizenship-test/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    problem:
      'Pass your Canadian citizenship test with confidence. Canadian Citizenship Test 2026 is a complete, offline study kit built on the official IRCC guide Discover Canada: The Rights and Responsibilities of Citizenship: 1,000+ multiple-choice and true/false practice questions with explanations and chapter references, all 10 study chapters in 48 illustrated lessons, realistic timed mock tests in the real format (20 questions, 45 minutes, pass at 15/20), province- and territory-specific questions for all 13, a 200+ term glossary with pronunciation, text-to-speech read-aloud, and progress tracking with a readiness score and weak-area analysis. Free with a one-time Premium unlock — no subscription, no account, no ads, no tracking. Not affiliated with Immigration, Refugees and Citizenship Canada (IRCC) or the Government of Canada.',
    features: [
      { name: '1,000+ Practice Questions', description: 'Multiple choice and true/false, each with an explanation and a reference to the Discover Canada chapter it comes from.' },
      { name: 'All 10 Chapters, 48 Lessons', description: 'The whole official guide as illustrated lessons — history, government, rights, symbols, regions and more.' },
      { name: 'Realistic Timed Mock Tests', description: 'The real test format: 20 questions, 45 minutes, pass at 15/20 — with a readiness score after each attempt.' },
      { name: 'Province & Territory Questions', description: 'Questions specific to your province or territory, covering all 13.' },
      { name: 'Glossary With Pronunciation', description: '200+ key terms with read-aloud, plus text-to-speech for every question and lesson.' },
      { name: 'Offline, No Account, No Ads', description: 'Works fully offline. One-time Premium unlock — no subscription, no tracking.' },
    ],
    techStack: [
      'Kotlin + Jetpack Compose',
      'Room + DataStore (offline-first)',
      'Google Play Billing + RevenueCat',
      'Android TTS',
      'WorkManager (local reminders)',
    ],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design', 'ai-agents'],
    relatedBlogSlugs: ['citizenship-test-prep-apps-us-canada-uk', 'mobile-app-development-cost-2026', 'how-to-choose-saas-development-partner'],
  },
  {
    slug: 'ludovest',
    name: 'Ludo Vest',
    title: 'Ludo Vest — Provably Fair Ludo Game',
    description:
      'Ludo Vest: a provably fair Ludo game with five variations, live clubs, voice chat, gifts, and offline play. Built and shipped by Orvexaco.',
    tagline: 'Play Ludo you can verify.',
    oneLiner: 'Provably fair Ludo with five variations, live clubs, voice chat, gifts, and offline play.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.ludovest.app',
    websiteUrl: 'https://www.ludovest.online',
    logoSrc: '/logos/ludovest-160.webp',
    faviconSrc: '/logos/ludovest-icon-32.webp',
    primaryKeyword: 'provably fair Ludo game',
    theme: 'dark',
    platform: 'android',
    problem:
      'Most Ludo games ask players to trust the dice and choose between social play and a fair game. Ludo Vest brings both together with verifiable dice, five variations, live clubs, voice chat, gifting, and offline play in one mobile-first experience.',
    features: [
      { name: 'Provably Fair Dice', description: 'Verify every roll instead of taking the game on faith.' },
      { name: 'Five Game Variations', description: 'Classic Ludo plus four variations built for quick, replayable matches.' },
      { name: 'Live Clubs', description: 'Play with friends in clubs with voice chat and social features.' },
      { name: 'Offline Play', description: 'Keep playing when you are away from a connection.' },
      { name: 'Gifts & Rewards', description: 'Collect gifts, hearts, and progression rewards as you play.' },
    ],
    screenshots: [
      { src: '/screenshots/ludovest/01-home.webp', alt: 'Ludo Vest home screen' },
      { src: '/screenshots/ludovest/02-ludo.webp', alt: 'Ludo Vest classic Ludo match' },
      { src: '/screenshots/ludovest/03-club.webp', alt: 'Ludo Vest live club with voice chat' },
      { src: '/screenshots/ludovest/04-profile.webp', alt: 'Ludo Vest player profile' },
      { src: '/screenshots/ludovest/05-dominos.webp', alt: 'Ludo Vest Dominos game' },
      { src: '/screenshots/ludovest/06-jackaroo.webp', alt: 'Ludo Vest Jackaroo game' },
      { src: '/screenshots/ludovest/07-hot-clubs.webp', alt: 'Ludo Vest hot clubs list' },
      { src: '/screenshots/ludovest/08-ranks.webp', alt: 'Ludo Vest player rankings' },
    ],
    techStack: ['Flutter', 'Node.js + Express', 'PostgreSQL', 'WebSockets', 'AWS'],
    builtWithServiceSlugs: ['mobile-app-development', 'custom-saas-development', 'devops'],
    relatedBlogSlugs: ['provably-fair-ludo-how-ludo-vest-works'],
  },
  {
    slug: 'balancely',
    name: 'Balancely',
    title: 'Balancely — Canadian PDF Statements to Reconciled Excel',
    description:
      'Balancely turns Canadian bank and credit-card PDFs into reconciled Excel workbooks with AI categorization and QuickBooks export. Built and shipped by Orvexaco.',
    tagline: 'Statements in. Clean books out.',
    oneLiner: 'Turn Canadian bank and credit-card PDFs into reconciled Excel workbooks in minutes.',
    liveUrl: 'https://balancely.ca',
    logoSrc: '/logos/balancely.svg',
    faviconSrc: '/logos/balancely.svg',
    primaryKeyword: 'PDF to Excel bank statement converter',
    theme: 'light',
    problem:
      'Transcribing bank and credit-card statements by hand is slow, error-prone, and hard to reconcile. Balancely extracts transactions from Canadian PDFs, categorizes them, checks the totals, and exports clean workbooks and accounting-ready files.',
    features: [
      { name: 'Multi-Statement Uploads', description: 'Process up to six bank or credit-card PDFs together.' },
      { name: 'Reconciliation Checks', description: 'Cross-check every total and flag even small discrepancies.' },
      { name: 'AI Categorization', description: 'Categorize transactions with vendor memory for future statements.' },
      { name: 'Excel, CSV & QBO Export', description: 'Download clean workbooks, CSV files, or QuickBooks Web Connect files.' },
      { name: 'Private Processing', description: 'Process statement transactions in memory without storing every line item.' },
    ],
    techStack: ['Next.js + React', 'Google Gemini', 'PostgreSQL', 'ExcelJS', 'Stripe'],
    builtWithServiceSlugs: ['ai-agents', 'custom-saas-development', 'devops'],
    relatedBlogSlugs: ['convert-bank-statement-pdf-to-excel-canada'],
  },
  {
    slug: 'uk-citizenship-test',
    name: 'UK Citizenship Test 2026',
    title: 'UK Citizenship Test 2026 — Life in the UK Test Prep App',
    description:
      'UK Citizenship Test 2026: offline Life in the UK test preparation with official handbook-based lessons, practice questions, mock tests, and progress tracking. Built and shipped by Orvexaco.',
    tagline: 'Pass the Life in the UK Test with confidence.',
    oneLiner: 'Offline Life in the UK test prep with official lessons, practice questions, mock tests, and progress tracking.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.ukcitizenshiptest.app',
    logoSrc: '/logos/uk-citizenship-test.svg',
    faviconSrc: '/logos/uk-citizenship-test.svg',
    primaryKeyword: 'UK citizenship test app',
    theme: 'dark',
    platform: 'android',
    legal: { privacyPath: '/uk-citizenship-test/privacy', termsPath: '/uk-citizenship-test/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    problem:
      'The Life in the UK Test covers a wide official handbook and rewards consistent revision, but many study tools are noisy, ad-heavy, or require an account. UK Citizenship Test 2026 keeps the preparation focused with offline lessons, practice, realistic mock tests, and progress tracking.',
    features: [
      { name: 'Official Handbook-Based Lessons', description: 'Study the key chapters and facts from the official Life in the UK handbook.' },
      { name: 'Practice Questions', description: 'Build confidence with focused practice across the test topics.' },
      { name: 'Realistic Mock Tests', description: 'Rehearse the exam format and track your best scores.' },
      { name: 'Four Nations Coverage', description: 'Learn facts and questions covering England, Scotland, Wales, and Northern Ireland.' },
      { name: 'Offline, No Account, No Ads', description: 'Revise anywhere without an account, adverts, or a connection.' },
    ],
    techStack: ['Kotlin + Jetpack Compose', 'Room', 'DataStore', 'Google Play Billing', 'Android TTS'],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design', 'ai-agents'],
    relatedBlogSlugs: ['citizenship-test-prep-apps-us-canada-uk'],
  },
  {
    slug: 'arrow-escape',
    name: 'Arrow Escape: Calm Puzzle',
    title: 'Arrow Escape: Calm Puzzle — A Calm Logic Puzzle for Android',
    description:
      'Arrow Escape: Calm Puzzle is a calm logic puzzle for Android by OrvexaCo. Slide each arrow the way it points when its whole path is clear. No ads, no account, and fully offline.',
    tagline: 'Clear the board, calmly.',
    oneLiner: 'A calm logic puzzle where every arrow slides the way it points, only when its whole path is clear.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.orvexaco.arrowpuzzle',
    logoSrc: '/logos/arrow-escape-160.webp',
    faviconSrc: '/logos/arrow-escape-icon-32.webp',
    primaryKeyword: 'calm logic puzzle Android app',
    theme: 'light',
    platform: 'android',
    legal: { privacyPath: '/arrow-escape/privacy', termsPath: '/arrow-escape/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    heroNote: 'No ads · Offline · No sign-in',
    screenshotSlots: 6,
    problem:
      'Arrow Escape: Calm Puzzle is a calm logic puzzle. Each arrow slides the way it points, only if its whole path is clear. It includes Classic mode with 3 lives and Relax mode with unlimited lives, both free, plus 240 curated levels across Easy, Normal, Hard and Expert, endless levels, and a Daily Challenge.',
    features: [
      { name: 'No ads, ever', description: 'The app contains no ads of any kind.' },
      { name: 'No forced lives or timers', description: 'Classic mode has 3 lives and Relax mode has unlimited lives; both modes are free.' },
      { name: 'Fair, precise controls', description: 'Each arrow slides the way it points only when its whole path is clear.' },
      { name: 'Hundreds of levels plus a Daily Challenge', description: '240 curated levels across Easy, Normal, Hard and Expert, plus endless levels and a Daily Challenge.' },
      { name: 'Easy on the eyes, with a free dark theme', description: 'A calm look that is easy on the eyes, with a free dark theme included.' },
      { name: 'Fair one-time purchases', description: 'Optional one-time theme packs, Arrow Styles, hint packs, or Everything Pack. No subscriptions, coins or energy.' },
    ],
    techStack: ['Android', 'Google Play Billing', 'Offline-first'],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design'],
    relatedBlogSlugs: ['arrow-escape-calm-puzzle-no-ads'],
  },
  {
    slug: 'police-chief',
    name: 'Police Chief: City Justice',
    title: 'Police Chief: City Justice — Police Station Tycoon Game for Android',
    description:
      'Police Chief: City Justice is an offline police station tycoon game for Android by OrvexaCo. Build your precinct, dispatch patrols, interrogate suspects and follow a long story. No ads, no account.',
    tagline: 'Build the precinct. Clean up the city.',
    oneLiner: 'An offline police station tycoon: build your precinct, take cases, interrogate suspects and follow a story across 21 districts.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.orvexaco.policechief',
    logoSrc: '/logos/police-chief-160.webp',
    faviconSrc: '/logos/police-chief-icon-32.webp',
    primaryKeyword: 'police station tycoon game Android',
    theme: 'dark',
    platform: 'android',
    legal: { privacyPath: '/police-chief/privacy', termsPath: '/police-chief/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    heroNote: 'No ads · Offline · No sign-in',
    problem:
      'Police Chief: City Justice puts you in charge of the worst precinct in Harbor City. Answer 911 calls, dispatch patrol cars, interrogate suspects before they lawyer up, and build the station room by room. It plays fully offline, keeps earning for up to 8 hours while you are away, and has no ads and no loot boxes.',
    features: [
      { name: 'Build your precinct', description: 'Holding cells, a detective bureau, a patrol garage, an armory, a prison wing and more, each with upgrades.' },
      { name: 'Take cases', description: 'Answer 911 calls, dispatch patrol cars and see every case through from arrest to charge.' },
      { name: 'Interrogate suspects', description: 'Rapport, pressure, evidence or a bluff: get the confession before the suspect asks for a lawyer.' },
      { name: 'Recruit officers', description: 'Patrol officers, detectives, guards and clerks, each with their own stats, traits and gear.' },
      { name: 'A long story', description: 'More than 190 chapters across five eras and 21 districts, from a harbor pickpocket to a city-wide conspiracy.' },
      { name: 'Fair purchases', description: 'No ads and no loot boxes. Optional packs show exactly what they contain, and the whole story is playable for free.' },
    ],
    screenshots: [
      { src: '/screenshots/police-chief/01-precinct.webp', alt: 'Police Chief precinct with rooms, patrol cars and the 911 call button' },
      { src: '/screenshots/police-chief/02-cases.webp', alt: 'Police Chief case board with active cases' },
      { src: '/screenshots/police-chief/03-interrogation.webp', alt: 'Police Chief interrogation room with tactic cards' },
      { src: '/screenshots/police-chief/04-officers.webp', alt: 'Police Chief officer roster' },
      { src: '/screenshots/police-chief/05-story.webp', alt: 'Police Chief story chapter introduction' },
      { src: '/screenshots/police-chief/06-case-result.webp', alt: 'Police Chief full confession case result' },
      { src: '/screenshots/police-chief/07-district.webp', alt: 'Police Chief district transfer screen' },
      { src: '/screenshots/police-chief/08-shop.webp', alt: 'Police Chief shop with the daily gift and starter pack' },
    ],
    techStack: ['Unity', 'Android', 'Google Play Billing', 'RevenueCat', 'Offline-first'],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'repzo',
    name: 'REPZO',
    title: 'REPZO: Gym Workout Tracker — Offline Strength Training Log for Android',
    description:
      'REPZO is an offline-first gym and strength-training tracker for Android: routines and programs, fast set logging with a rest timer, personal records, progress charts, and a plate calculator. No account, no ads, no tracking. Available on Google Play, built by Orvexaco.',
    tagline: 'Log every rep. Keep every record.',
    oneLiner: 'Offline-first gym and strength-training tracker: routines, fast set logging, rest timer, personal records, and progress charts.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.orvexaco.repzo',
    logoSrc: '/logos/repzo-160.webp',
    faviconSrc: '/logos/repzo-icon-32.webp',
    primaryKeyword: 'offline gym workout tracker app',
    theme: 'dark',
    platform: 'android',
    legal: { privacyPath: '/repzo/privacy', termsPath: '/repzo/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    heroNote: 'No account · No ads · Works offline',
    problem:
      'Most workout trackers want an account, a connection, and your data on their servers before you can log a single set. REPZO is an offline-first gym and strength-training tracker for Android that keeps your workout, body, and profile data on your device: build routines and follow programs, log sets fast with a rest timer and a lock-screen notification, and see personal records, streaks, achievements, progress charts, muscle analytics, and body measurements. It is free with an optional REPZO Pro subscription billed through Google Play. No accounts, no ads, no analytics, no tracking.',
    features: [
      { name: 'Routines & Programs', description: 'Build your own routines or follow a program, then start a workout in a tap.' },
      { name: 'Fast Set Logging', description: 'Log sets quickly, with a rest timer and a lock-screen notification that keep the workout moving.' },
      { name: 'Records, Streaks & Achievements', description: 'Personal records are tracked as you lift, alongside streaks and achievements.' },
      { name: 'Progress & Muscle Analytics', description: 'Progress charts, muscle analytics, and body measurements show how training is going.' },
      { name: 'Plate Calculator', description: 'Works out which plates to load on the bar for the weight you want.' },
      { name: 'Your Data, Your Device', description: 'Export and import as JSON or CSV, back up to a location you choose, and optionally sync with Health Connect.' },
    ],
    techStack: [
      'Kotlin + Jetpack Compose',
      'Room + DataStore (offline-first)',
      'Google Play Billing + RevenueCat',
      'Health Connect',
      'WorkManager',
    ],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design'],
    relatedBlogSlugs: ['mobile-app-development-cost-2026'],
  },
  {
    slug: 'digital-khata',
    name: 'Digital Khata: Udhar & Stock',
    title: 'Digital Khata: Udhar & Stock — Offline Shop Khata, Stock & Invoice App for Android',
    description:
      'Digital Khata: Udhar & Stock is an offline shop accounting app for Android: stock that updates itself, sales and purchases, customer and supplier udhar khata with WhatsApp and SMS reminders, invoices with your logo, cash, bank and staff books, and profit & loss. Free, with an optional one-time Premium Lifetime upgrade. No sign-up, no ads. Available on Google Play, built by Orvexaco.',
    tagline: 'Stock, sales, khata & invoices — offline, no sign-up.',
    oneLiner: 'Offline-first shop accounting: stock, sales and purchases, customer and supplier udhar khata, invoices with your logo, cash, bank and staff books, and profit & loss.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.orvexaco.digitalkhata',
    logoSrc: '/logos/digital-khata-160.webp',
    faviconSrc: '/logos/digital-khata-icon-32.webp',
    ogImage: '/og/digital-khata.jpg',
    primaryKeyword: 'digital khata udhar and stock app',
    theme: 'dark',
    platform: 'android',
    legal: { privacyPath: '/digital-khata/privacy', termsPath: '/digital-khata/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    heroNote: 'Free · Premium Lifetime US$9.99, one-time · No sign-up · No ads · Works offline',
    problem:
      'Most shops still run on a paper register, or on accounting apps that want an account, a connection, and your business data on their servers. Digital Khata: Udhar & Stock is an offline-first shop book for any shop — general store, grocery, clothing, electronics, pharmacy, hardware, sports, cosmetics or restaurant. Stock updates itself with every sale and purchase, the customer and supplier khata keeps track of who owes whom, and invoices, expenses, cash, bank and staff books and reports are all on the phone. It is a free download with an optional one-time Premium Lifetime upgrade (US$9.99, or the local price in Google Play). No sign-up, no ads, no tracking: your data stays on your phone, and Premium can back it up to your own Google Drive.',
    features: [
      { name: 'Stock That Updates Itself', description: 'Stock goes down with every sale and up with every purchase, with low-stock alerts and a suggested reorder quantity.' },
      { name: 'Sales & Purchases', description: 'Record sales and purchase bills in seconds, paid or unpaid; totals and profit are calculated for you.' },
      { name: 'Udhar Khata With Reminders', description: 'Balances, payments and full history for every customer and supplier, with WhatsApp and SMS payment reminders in one tap.' },
      { name: 'Invoices With Your Logo', description: 'Professional invoices with your shop logo and details: share as PDF, print, or send on WhatsApp. Also a business card maker.' },
      { name: 'Cash, Bank & Staff Books', description: 'Cash book and bank book, plus a staff book for attendance, salary and advances. Daily and monthly expenses too.' },
      { name: 'Reports & Profit and Loss', description: 'Sales, purchases, expenses, stock and dues reports with profit & loss and PDF export. Premium adds yearly and custom-range profit & loss.' },
      { name: 'Private & Locked', description: 'No sign-up, no ads, no tracking. PIN or fingerprint app lock, 10 currencies, auto-backup on the phone and a backup/restore file.' },
      { name: 'Premium Lifetime — US$9.99 once', description: 'Multiple businesses, Google Drive backup & sync to your own account, Excel export, yearly and custom profit & loss, and invoices without the "Generated by" line.' },
    ],
    screenshotLayout: 'scroll',
    screenshots: [
      { src: '/screenshots/digital-khata/01-dashboard.webp', alt: 'Digital Khata dashboard with today\'s sales, profit and quick actions' },
      { src: '/screenshots/digital-khata/02-new-sale.webp', alt: 'Recording a new sale that updates stock automatically' },
      { src: '/screenshots/digital-khata/03-khata.webp', alt: 'Customer khata with balance, payments and history' },
      { src: '/screenshots/digital-khata/04-low-stock.webp', alt: 'Stock list with low-stock alerts' },
      { src: '/screenshots/digital-khata/05-invoices.webp', alt: 'Professional invoice with the shop logo' },
      { src: '/screenshots/digital-khata/06-profit-loss.webp', alt: 'Profit and loss summary for a date range' },
      { src: '/screenshots/digital-khata/07-expenses.webp', alt: 'Daily and monthly expense tracking' },
      { src: '/screenshots/digital-khata/08-reports.webp', alt: 'Reports with PDF and Excel export' },
    ],
    techStack: [
      'Flutter + Dart',
      'Riverpod',
      'Hive (offline-first local database)',
      'PDF & Excel export',
      'Google Play Billing + RevenueCat',
      'Google Drive API (appDataFolder)',
    ],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design'],
    relatedBlogSlugs: ['mobile-app-development-cost-2026'],
  },
  {
    slug: 'sudoku-cove',
    name: 'Sudoku Cove',
    title: 'Sudoku Cove — Free Offline Sudoku Game for Android',
    description:
      'Sudoku Cove is a free offline Sudoku game for Android by OrvexaCo, with endless levels, seasons, daily challenges and explained hints. No ads and no account; every puzzle and hint is free, with optional Cove Premium and coin packs.',
    tagline: 'A calm home for every puzzle.',
    oneLiner: 'A free offline Sudoku game with endless levels, seasons, daily challenges and hints that explain the next step.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.orvexaco.sudokucove',
    logoSrc: '/logos/sudoku-cove-160.webp',
    faviconSrc: '/logos/sudoku-cove-icon-32.webp',
    primaryKeyword: 'free offline Sudoku game Android',
    theme: 'light',
    platform: 'android',
    status: 'coming-soon',
    legal: { privacyPath: '/sudoku-cove/privacy', termsPath: '/sudoku-cove/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    heroNote: 'No ads · Offline play · No sign-in',
    problem:
      'Most free Sudoku apps interrupt the puzzle with ads, limit your hints or ask you to pay to keep going. In Sudoku Cove every puzzle, level, hint and daily challenge is free, with no ads; Cove Premium and coin packs are optional. It keeps the board large and readable, explains each hint, generates endless puzzles on the device, and works fully offline without an account.',
    features: [
      { name: 'Free and unlimited', description: 'No ads and no energy timers. Every level, mode, hint and daily challenge is free; Cove Premium and coin packs are optional.' },
      { name: 'Endless levels', description: 'A Journey of generated levels with chapters and boss levels that never runs out.' },
      { name: 'Seasons and challenges', description: 'New seasons with reward tracks, a Daily Challenge, weekly puzzle packs, quests and rule-changing challenges.' },
      { name: 'Classic, Giant and Shapes', description: 'Classic 9×9, Mini 6×6, Giant 16×16 and irregular Shapes boards, from Easy to Extreme.' },
      { name: 'Hints that explain', description: 'Smart Hint names the technique and shows why a number fits, so you learn as you play.' },
      { name: 'Built for long sessions', description: 'Pencil notes, auto notes, undo, seven themes, dark mode and accessibility options.' },
    ],
    techStack: ['Kotlin', 'Jetpack Compose', 'Android', 'Offline-first'],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'aqua-bliss',
    name: 'Aqua Bliss: Water Sort',
    title: 'Aqua Bliss: Water Sort — Free Offline Water Sort Puzzle for Android',
    description:
      'Aqua Bliss: Water Sort is a free offline water sort puzzle game for Android by OrvexaCo, with 10,000+ generated levels, daily challenges and eight game modes. No ads, no purchases and no account.',
    tagline: 'Pour, sort and unwind.',
    oneLiner: 'A calm offline water sort puzzle with 10,000+ levels, daily challenges, eight game modes and collectible themes and bottles.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.aquabliss.watersort',
    logoSrc: '/logos/aqua-bliss-160.webp',
    faviconSrc: '/logos/aqua-bliss-icon-32.webp',
    primaryKeyword: 'offline water sort puzzle game Android',
    theme: 'dark',
    platform: 'android',
    status: 'coming-soon',
    legal: { privacyPath: '/aqua-bliss/privacy', termsPath: '/aqua-bliss/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    heroNote: 'No ads · No purchases · Offline · No sign-in',
    problem:
      'Most water sort games interrupt the puzzle with ads, or need a connection to keep going. Aqua Bliss generates its levels on the device, checks that each one can be solved, and works fully offline without an account or an ad in sight.',
    features: [
      { name: 'Endless levels', description: 'More than 10,000 generated levels across seven difficulty tiers, from Beginner to Extreme.' },
      { name: 'Eight game modes', description: 'Classic, Relax, Daily Challenge, Limited Moves, Mystery Bottles, Color Collection, Expert Challenge and a Weekly Event.' },
      { name: 'Helpful tools', description: 'Undo, hints, an extra bottle, shuffle and auto-solve for the moments you get stuck.' },
      { name: 'Themes and bottles', description: 'Twelve themes and twenty bottle designs to unlock with coins, gems and achievements.' },
      { name: 'Daily rewards', description: 'Daily puzzles, login rewards, streaks, achievements and a color album to complete.' },
      { name: 'Built for everyone', description: 'Color-blind palette, liquid symbols, high contrast and reduced motion options.' },
    ],
    techStack: ['Kotlin', 'Jetpack Compose', 'Android', 'Offline-first'],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'wonderfind',
    name: 'WonderFind — Hidden Object Adventures',
    title: 'WonderFind — Hidden Object Adventures: Relaxing Offline Hidden Object Game for Android',
    description:
      'WonderFind — Hidden Object Adventures is a free, relaxing hidden object game for Android by OrvexaCo, with illustrated scenes across ten worlds, endless generated levels, a daily puzzle and free unlimited hints. No ads, no timers and no account.',
    tagline: 'Look closer. Find wonder.',
    oneLiner: 'A calm offline hidden object game with illustrated scenes, endless validated levels, a daily puzzle and free unlimited hints.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.orvexaco.wonderfind',
    logoSrc: '/logos/wonderfind-160.webp',
    faviconSrc: '/logos/wonderfind-icon-32.webp',
    primaryKeyword: 'offline hidden object game Android',
    theme: 'dark',
    platform: 'android',
    status: 'coming-soon',
    legal: { privacyPath: '/wonderfind/privacy', termsPath: '/wonderfind/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    heroNote: 'No ads · No timers · Offline · No sign-in',
    problem:
      'Many hidden object games rush you with timers, lock hints behind payments or need a connection to keep going. WonderFind lets you search at your own pace: hints are free and unlimited, there are no timers or fail states, and every level is generated and checked on the device so it works fully offline.',
    features: [
      { name: 'Ten illustrated worlds', description: 'Cozy Home, Magical Workshop, Pirate Adventure, Secret Garden, Ancient Castle and more, with new worlds unlocking as you earn stars.' },
      { name: 'Endless levels', description: 'Chapters of twelve levels without end, each scene arranged on the device and checked so every object can be found.' },
      { name: 'Free unlimited hints', description: 'A hint is always one tap away and never costs anything.' },
      { name: 'Zoom and explore', description: 'Pinch, double-tap or use the buttons to zoom up to 400% and pan across every detail.' },
      { name: 'Daily puzzle and rewards', description: 'A fresh daily scene with a streak, plus stars, a 125-object album and ten badges to collect.' },
      { name: 'Built for everyone', description: 'Left-handed controls, high contrast markers, a larger object tray and calm, static visuals.' },
    ],
    techStack: ['Kotlin', 'Jetpack Compose', 'Android', 'Offline-first'],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'xo-arena',
    name: 'XO Arena',
    title: 'XO Arena — Free Offline Tic Tac Toe Game for Android',
    description:
      'XO Arena is a free offline Tic Tac Toe and board-game collection for Android by OrvexaCo, with seven game modes, three AI opponents, local two-player, 10,000 levels per game and monthly seasons. No ads and no account, with optional cosmetic packs.',
    tagline: 'Three in a row, endless ways to play.',
    oneLiner: 'An offline Tic Tac Toe collection with seven game modes, three AI opponents, local two-player, 10,000 levels per game and monthly seasons.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.orvexaco.xoarena',
    logoSrc: '/logos/xo-arena-160.webp',
    faviconSrc: '/logos/xo-arena-icon-32.webp',
    primaryKeyword: 'offline Tic Tac Toe game Android',
    theme: 'light',
    platform: 'android',
    status: 'coming-soon',
    legal: { privacyPath: '/xo-arena/privacy', termsPath: '/xo-arena/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    heroNote: 'No ads · Offline · No sign-in · Optional cosmetic packs',
    problem:
      'Most Tic Tac Toe apps stop at a single 3×3 board, fill the screen with ads or need a connection to play. XO Arena brings classic and bigger boards, Misère, Timed and Ultimate Tic Tac Toe together in one app, with AI opponents for every skill level, and works fully offline without an account.',
    features: [
      { name: 'Seven ways to play', description: 'Classic 3×3, 4×4, 5×5 with four or five in a row, Misère, Misère 4×4, Timed and Ultimate Tic Tac Toe.' },
      { name: 'Three AI opponents', description: 'Pip on Easy, Vex on Medium and Nova on Hard, so beginners and experts both have a fair match.' },
      { name: 'Two players, one device', description: 'Pass the phone and play a friend locally, with your own player names and avatar colours.' },
      { name: '10,000 levels per game', description: 'A long level path for every game mode, plus daily challenges to come back to.' },
      { name: 'Seasons and achievements', description: 'Monthly seasons with cosmetic rewards, achievements, statistics and a full match history.' },
      { name: 'Make it yours', description: 'Light, dark and system themes, a choice of boards and symbols, and your own player names and avatar colours.' },
    ],
    techStack: ['Kotlin', 'Jetpack Compose', 'Android', 'Offline-first'],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'petalgrove',
    name: 'Petalgrove',
    title: 'Petalgrove — Match-3 Garden Puzzle Game for Android',
    description:
      'Petalgrove is a free casual match-3 puzzle game for Android by OrvexaCo. Swap and match flowers across 120 levels, earn stars and restore three garden areas with Aunt Marigold and friends. No ads and no account, with optional in-app purchases.',
    tagline: "Restore Aunt Marigold's garden, one bloom at a time.",
    oneLiner: 'A match-3 garden puzzle game with 120 levels, a story told through dialogue, pets to befriend and three garden areas to restore.',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.orvexaco.petalgrove',
    logoSrc: '/logos/petalgrove-160.webp',
    faviconSrc: '/logos/petalgrove-icon-32.webp',
    primaryKeyword: 'match-3 garden puzzle game Android',
    theme: 'light',
    platform: 'android',
    status: 'coming-soon',
    legal: { privacyPath: '/petalgrove/privacy', termsPath: '/petalgrove/terms' },
    supportEmail: 'muhammadsuleman30000@gmail.com',
    heroNote: 'No ads · Offline play · No sign-in · Optional purchases',
    problem:
      'Many match-3 games are built around ads, timers and constant prompts to pay. Petalgrove keeps the focus on a calm puzzle and a garden to bring back to life: every level and garden area can be earned by playing, gameplay works fully offline without an account, and there are no ads.',
    features: [
      { name: '120 puzzle levels', description: 'Swap and match flowers across 120 hand-tuned and generated levels, and earn stars as you go.' },
      { name: 'Restore the garden', description: "Spend stars to restore Marigold's Front Garden, The Old Orchard and Lakeside Walk." },
      { name: 'Story and pets', description: 'Meet Aunt Marigold, Wren, Basil and Juniper through dialogue, and befriend Sir Hops, Biscuit, Clover and Puddle.' },
      { name: 'Special pieces and boosters', description: 'Watering Can, Bloom Bomb, Sunburst and Rainbow Butterfly pieces, the Trowel, Rake and Hedge Shears boosters, and obstacles from weeds and crates to ice, vines and mud.' },
      { name: 'Something every day', description: 'Daily gifts, an offline daily challenge, weekly local events, a market-day mini-game and achievements.' },
      { name: 'Built for everyone', description: 'Reduced motion, high contrast, tile symbols, large text, and sound, music and haptics controls.' },
    ],
    techStack: ['Kotlin', 'Jetpack Compose', 'Android', 'Offline-first'],
    builtWithServiceSlugs: ['mobile-app-development', 'ui-ux-design'],
    relatedBlogSlugs: [],
  },
];
export const productsBySlug: Record<string, Product> = Object.fromEntries(
  products.map((p) => [p.slug, p]),
);
